import { Col, Row, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { axios, setLanguageHeader } from "../../utils/axios";

import { TranslationOutlined } from "@ant-design/icons";
import { Select } from "../../components/Select/Select";
import i18n, { LANGUAGE_OPTIONS, Locales } from "../../i18n";
import storage from "../../utils/storage";
import style from "./ZAMPublic.module.scss";

import to from "await-to-js";
import { TipObveznika } from "../../modules/obligee/exciseStamps/exciseStamps.types";
import { ApiResponseObject } from "../../types";

interface IQrCodeInfo {
  markicaSerijskiBroj: string;
  tipProizvoda: string;
  trziste: string;
  obveznikTip: number;
  pib: number | null;
  obveznikNaziv: string;
  nazivBrenda: string | null;
  mestoProizvodnje: string | null;
  proizvodnaLinija: string | null;
  datumProizvodnje: string | null;
  ambalaza: string | null;
}

const publicApiBaseUrl = "https://a.mfin.gov.rs/api";

export const ZAMPublic = () => {
  const { t } = useTranslation();

  const [qrCodeInfo, setQrCodeInfo] = useState<IQrCodeInfo | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getQrCodeData = async () => {
    const qrCode = window.location.href.substring(
      window.location.href.lastIndexOf("/") + 1
    );
    const [err, res] = await to<ApiResponseObject<IQrCodeInfo>>(
      axios.get("getZinKodInformation", {
        params: { zinKod: qrCode },
        baseURL: publicApiBaseUrl,
      })
    );
    if (err || !res.success) return setError(true);
    if (!res.data) return setErrorMessage(t("COMMON.KOD_NIJE_PRONADJEN"));

    setQrCodeInfo(res.data);
  };

  useEffect(() => {
    getQrCodeData();
  }, []);

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
    setLanguageHeader(value as Locales);
    storage.setItem("locale", value);
    getQrCodeData();
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <span className={style.logo}>eAkcize</span>

        <Select
          suffixIcon={<TranslationOutlined />}
          onChange={handleLanguageChange}
          value={storage.getItem("locale") || Locales.LATIN}
          className={style.languageSelect}
          options={LANGUAGE_OPTIONS}
          name="language"
        />
      </div>
      <div className={style.contentWrapper}>
        {!error && qrCodeInfo && (
          <div className={style.content}>
            <div className={style.contentHeader}>
              {t("ZAM_PUBLIC.PODACI_O_ARTIKLU")}
            </div>
            <div className={style.contentBody}>
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.SERIJSKI_BROJ_AM")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.markicaSerijskiBroj}
                </Col>
              </Row>
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.TIP_PROIZVODA")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.tipProizvoda}
                </Col>
              </Row>
              {qrCodeInfo?.nazivBrenda ? <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.NAZIV_BRENDA")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.nazivBrenda}
                </Col>
              </Row> : null}
              {qrCodeInfo?.ambalaza ? 
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.AMBALAZA")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.ambalaza}
                </Col>
              </Row> : null}
              {qrCodeInfo?.mestoProizvodnje ? <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.MESTO_PROIZVODNJE")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.mestoProizvodnje}
                </Col>
              </Row> : null}
              {qrCodeInfo?.proizvodnaLinija ? 
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.PROIZVODNA_LINIJA")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.proizvodnaLinija}
                </Col>
              </Row> : null}
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.TRZISTE")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.trziste}
                </Col>
              </Row>
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                    {qrCodeInfo?.obveznikTip === TipObveznika.PROIZVODJAC ? t("ZAM_PUBLIC.PROIZVODJAC") : (qrCodeInfo?.obveznikTip === TipObveznika.UVOZNIK ? t("ZAM_PUBLIC.UVOZNIK") : t("ZAM_PUBLIC.PROIZVODJAC_UVOZNIK"))}
                </Col>
                <Col span={24} className={style.fieldValue}>
                    {qrCodeInfo?.obveznikNaziv}
                </Col>
              </Row>
              {qrCodeInfo?.pib ? 
              <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.PIB")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.pib}
                </Col>
              </Row> : null}
              {qrCodeInfo?.datumProizvodnje ? <Row className={style.dataRow}>
                <Col span={24} className={style.label}>
                  {t("ZAM_PUBLIC.DATUM_PROIZVODNJE_CARINJENJA")}
                </Col>
                <Col span={24} className={style.fieldValue}>
                  {qrCodeInfo?.datumProizvodnje}
                </Col>
              </Row> : null}
            </div>
          </div>
        )}
        {!error && !qrCodeInfo && !errorMessage && (
          <div className={style.contentError}>
            <Spin className={style.spinner} size="large"></Spin>
          </div>
        )}
        {error && (
          <div className={style.contentError}>
            {t("ZAM_PUBLIC.GENERIC_ERROR")}
          </div>
        )}
        {errorMessage && (
          <div className={style.contentError}>{errorMessage}</div>
        )}
      </div>
    </div>
  );
};
